import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../service/auth-service';

/**
 * The body text the server sends when the login token is missing, expired or
 * forged: Nest's default for a guard that returns false (link-backend
 * `JwtGuard`).
 */
export const SESSION_REJECTED_TEXT = 'Forbidden resource';

/**
 * The guard's 403 for an account that was suspended or deleted while it was
 * signed in (link-backend `JwtGuard`, 20 Oct 2026). Also means "the session
 * is over".
 */
export const ACCOUNT_SUSPENDED_TEXT = 'this account is suspended';

const SESSION_OVER_TEXTS = [SESSION_REJECTED_TEXT, ACCOUNT_SUSPENDED_TEXT];

/**
 * True only for the guard's two 403s. Every other 403 is a business refusal with
 * its own message ("you cannot give tasks on behalf of that person") that the
 * screen shows as an error. Until 20 Oct 2026 every 403 logged the person out.
 */
export function isSessionRejected(error: HttpErrorResponse): boolean {
  if (error.status !== 403) return false;
  const body: unknown = error.error;
  const outer = body && typeof body === 'object' ? (body as { message?: unknown }).message : body;
  const text = outer && typeof outer === 'object' ? (outer as { message?: unknown }).message : outer;
  return typeof text === 'string' && SESSION_OVER_TEXTS.includes(text.trim());
}

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {

  constructor(private authService: AuthService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (isSessionRejected(error)) {
          // Clear session and redirect to login
          this.authService.forceLogout();
        }
        return throwError(() => error);
      })
    );
  }
}
